
import UpdateProductSetInfos from '@/components/updateProductSetInfos/UpdateProductSetInfos.vue'
import { CategoryWithProductSetInfoKeysStructure } from '@/views/productPackageView/types'
import ProductSetActivitiesList from './ProductSetActivitiesList.vue'
import { ProductSetInfoKeyType } from '@/__generated__/globalTypes'
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import {
  UpdateMultipleProductSetPricesDocument,
  UpdateMultipleProductSetPricesMutation,
  UpdateMultipleProductSetPricesMutationVariables
} from './gql/__generated__/bulkUpdateProductSets.mutation'
import { ProductSetInfoKeyModel } from './types'
import { CombinationProductSetActivityFragment } from '@/views/productPackageView/gql/__generated__/combinationProductSetActivities.query'
import { AssignedProductSetActivityFragment } from './gql/__generated__/getProductSetList.query'
import SwitchTooltip from '@/components/switchTooltip/SwitchTooltip.vue'

@Component({
  components: {
    UpdateProductSetInfos,
    ProductSetActivitiesList,
    SwitchTooltip
  }
})
export default class ProductSetBulkUpdateModal extends Vue {
  @Prop() show!: boolean
  @Prop() categoryWithProductSetFields?: CategoryWithProductSetInfoKeysStructure | null
  @Prop() productSetIds!: string[]
  @Ref() bulkUpdateProductSetForm?: HTMLFormElement
  @Prop() isNoProductNeeded?: boolean | null
  @Prop() combinationProductSetActivities?: CombinationProductSetActivityFragment[]
  mProductSetBrandlineId = ''

  isLoading = false

  shouldReplaceAllActivities = false

  mActivities: AssignedProductSetActivityFragment[] = []

  // Structure to save
  mProductSetInfoKeys: ProductSetInfoKeyModel[] = []

  ProductSetInfoKeyType = ProductSetInfoKeyType

  rules = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    moneyOrEmpty: (value: any) => {
      // Can contain numbers, commas and dots. Or is a null value (nothing).
      const regex = /^[0-9]{0,9}([,.][0-9]{0,2})?$/
      return regex.test(value) || this.$t('productPackage.edit.category.productSets.upsert.validations.money')
    }
  }

  get productPackageCategoryId() {
    return this.categoryWithProductSetFields?.id
  }

  @Watch('show')
  onshowUpdate() {
    this.resetForm()
  }

  addActivity(activity: CombinationProductSetActivityFragment, isForStandardProductSet: boolean) {
    if (
      !this.mActivities.some(
        (x) => x.activity?.id === activity?.id && x.isForStandardProductSet === isForStandardProductSet
      )
    ) {
      this.mActivities.push({
        id: '',
        activity,
        isForStandardProductSet
      })
    }
  }

  removeActivity(assignedProductSetActivity: AssignedProductSetActivityFragment, isForStandardProductSet: boolean) {
    this.mActivities = this.mActivities.filter(
      (x) =>
        !(
          x.activity?.id === assignedProductSetActivity.activity?.id &&
          x.isForStandardProductSet === isForStandardProductSet
        )
    )
  }

  resetForm() {
    // Set structure for the info keys
    this.mProductSetInfoKeys =
      this.categoryWithProductSetFields?.productSetInfoKeys?.map(
        (infoKeyStructure) =>
          ({
            ...infoKeyStructure,
            value: null
          } as ProductSetInfoKeyModel)
      ) ?? []

    this.mActivities = []
    this.mProductSetBrandlineId = ''
    this.shouldReplaceAllActivities = false
  }

  getProductSetsMutationData(): UpdateMultipleProductSetPricesMutationVariables {
    return {
      productPackageCategoryId: this.productPackageCategoryId,
      productSetIds: this.productSetIds,
      productSetInfos:
        this.mProductSetInfoKeys
          .filter((x) => !!x.value)
          .map((x) => ({
            item1: x.id,
            item2: x.value?.toString() ?? ''
          })) ?? [],
      brandlineId: this.mProductSetBrandlineId || null,
      activities: this.mActivities.map((x) => ({
        item1: x.activity?.id ?? '',
        item2: x.isForStandardProductSet
      })),
      shouldReplaceAllActivities: this.shouldReplaceAllActivities
    }
  }

  async updateProductSets() {
    if (!this.bulkUpdateProductSetForm?.validate()) return

    const hasProductSetInfoChanges = this.mProductSetInfoKeys.some((x) => !!x.value)
    const hasActivityChanges = this.mActivities.length > 0
    const hasBrandlineChange = !!this.mProductSetBrandlineId

    const hasAnyChanges = hasProductSetInfoChanges || hasActivityChanges || hasBrandlineChange

    if (!hasAnyChanges) {
      return
    }
    const requestData = this.getProductSetsMutationData()

    this.isLoading = true
    try {
      await this.bulkUpdateProductSets(requestData)

      this.$emit('close', true)
    } catch {
      this.$emit('close')
    } finally {
      this.isLoading = false
    }
  }

  async bulkUpdateProductSets(variables: UpdateMultipleProductSetPricesMutationVariables) {
    await this.$apolloMutate<UpdateMultipleProductSetPricesMutation, UpdateMultipleProductSetPricesMutationVariables>({
      mutation: UpdateMultipleProductSetPricesDocument,
      variables,
      error: 'E4197'
    })
  }
}
